import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { MARKDOWN_COMPONENTS } from '../../../ui-components/MarkdownAfbeelding';
import MailNotitieTekst, { isMailNotitie } from '../../../ui-components/MailNotitieTekst';
import NotitieEditor from '../../../ui-components/NotitieEditor';
import {
  Box,
  Typography,
  Tooltip,
  TextField,
} from '@mui/material';
import { T, Icon, stitchInputSx, stitchGradientBtn, stitchOutlineBtn, stitchTooltipSlotProps, StitchIconBtn } from './stitch';
import { formatDate } from './projectMeta';


// Markdown-styling voor het Airtable richText-veld Notes (koppen, lijsten, bold).
const noteMarkdownSx = {
  fontFamily: T.fontBody,
  color: T.outline,
  fontSize: '0.875rem',
  lineHeight: 1.7,
  '& p': { m: 0, mb: 1 },
  '& p:last-child': { mb: 0 },
  '& ul, & ol': { my: 0.5, pl: 2.5 },
  '& li': { mb: 0.25 },
  '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
  '& h1, & h2, & h3, & h4': {
    fontFamily: T.font, fontWeight: 700, color: T.text,
    fontSize: '0.95rem', mt: 1.5, mb: 0.5,
  },
  '& strong': { color: T.text, fontWeight: 600 },
  '& code': { backgroundColor: 'rgba(0,0,0,0.06)', px: 0.5, borderRadius: '4px' },
  '& a': { color: T.primary },
  '& blockquote': { borderLeft: `3px solid ${T.amber}`, pl: 1.5, ml: 0, color: T.outlineVariant },
};


// Note Card
function NoteCard({ note, onDelete, onSave }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const title = note.fields?.Titel || note.fields?.Onderwerp || 'Notitie';
  // Reparatie voor notities die via het oude één-regelige invoerveld zijn
  // geplakt: regeleinden waren daar weggevallen, waardoor ## en lijstjes als
  // één lap tekst renderden. Alleen toepassen als de tekst er platgeslagen
  // uitziet (lang, nauwelijks regeleinden, wél markdown-tekens).
  const herstelPlatgeslagen = (t) => {
    const s = String(t || '');
    // Alleen bij duidelijk platgeslagen tekst: lang, nauwelijks regeleinden én
    // een ##-kop MIDDEN in een regel (dat komt in normale markdown niet voor).
    if (s.length < 300 || (s.match(/\n/g) || []).length > 3 || !/\S[ \t]+#{2,4}\s/.test(s)) return s;
    return s
      .replace(/\s*(#{2,4})\s+/g, '\n\n$1 ')
      .replace(/\s_\s/g, '\n- ')
      .trim();
  };
  // Herstel is ALLEEN weergave — bewerken/opslaan gebeurt altijd op de ruwe
  // tekst, zodat een false positive nooit permanent wordt.
  const rawContent = note.fields?.Notes || '';
  const content = herstelPlatgeslagen(rawContent);
  const [draftTitle, setDraftTitle] = useState(title);
  const [draftContent, setDraftContent] = useState(rawContent);
  const date = formatDate(note.fields?.Aanmaakdatum || note.fields?.Aangemaakt);
  const author = note.fields?.['Created By']?.name || note.fields?.Submitters?.[0] || '';

  const startEdit = (e) => {
    e.stopPropagation();
    setDraftTitle(title);
    setDraftContent(rawContent);
    setEditing(true);
    setExpanded(true);
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    setSaving(true);
    try {
      await onSave?.(note.id, { title: draftTitle, notes: draftContent });
      setEditing(false);
    } catch {
      // Fout is al getoast door de handler — bewerkmodus open laten zodat je opnieuw kunt proberen.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      onClick={() => { if (!editing) setExpanded(!expanded); }}
      sx={{
        p: 3,
        borderRadius: '0.75rem',
        backgroundColor: T.amberBg,
        border: `1px solid ${T.amber}40`,
        cursor: editing ? 'default' : 'pointer',
        transition: 'all 0.15s ease',
        '&:hover': { borderColor: T.amber, backgroundColor: editing ? T.amberBg : '#fef3c7' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 2 }}>
        {editing ? (
          <TextField
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            size="small"
            fullWidth
            label="Onderwerp"
            sx={stitchInputSx}
          />
        ) : (
          <Typography variant="subtitle2" sx={{ fontFamily: T.font, fontWeight: 600 }}>{title}</Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
          {!editing && author && <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>{author}</Typography>}
          {!editing && date && <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>{date}</Typography>}
          {onSave && !editing && (
            <Tooltip title="Bewerken" slotProps={stitchTooltipSlotProps}>
              <StitchIconBtn
                onClick={startEdit}
                sx={{ width: 28, height: 28, color: T.outlineVariant, '&:hover': { color: T.primary } }}
              >
                <Icon name="edit" size={18} />
              </StitchIconBtn>
            </Tooltip>
          )}
          {onDelete && !editing && (
            <StitchIconBtn
              onClick={(e) => { e.stopPropagation(); onDelete(note.id); }}
              sx={{ width: 28, height: 28, color: T.outlineVariant, '&:hover': { color: T.error } }}
            >
              <Icon name="delete_outline" size={18} />
            </StitchIconBtn>
          )}
        </Box>
      </Box>

      {editing ? (
        <Box onClick={(e) => e.stopPropagation()}>
          <NotitieEditor value={draftContent} onChange={setDraftContent} minHeight={120} autoFocus />
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outlineVariant, display: 'block', mt: 0.5 }}>
            Markdown wordt ondersteund (**vet**, - lijstjes, ## koppen)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <Box component="button" onClick={handleSave} disabled={saving} sx={stitchGradientBtn}>
              {saving ? 'Opslaan…' : 'Opslaan'}
            </Box>
            <Box component="button" onClick={(e) => { e.stopPropagation(); setEditing(false); }} sx={stitchOutlineBtn}>
              Annuleren
            </Box>
          </Box>
        </Box>
      ) : content && (
        <Box sx={{
          ...noteMarkdownSx,
          ...(!expanded && {
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
          }),
        }}>
          {isMailNotitie(content) ? <MailNotitieTekst tekst={content} onderwerp={note?.fields?.Onderwerp || note?.fields?.Titel || ''} /> : <ReactMarkdown components={MARKDOWN_COMPONENTS}>{content}</ReactMarkdown>}
        </Box>
      )}
    </Box>
  );
}

export default NoteCard;
